/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import ToastService from "../../services/toastService/ToastService";
import { profileSettings } from "../profileSettings/config/profileSettingsConfiguration.data";

import {
  useLazyGetHomeCompletedTaskListQuery,
  useLazyGetHomeDueTodayTaskListQuery,
  useLazyGetHomeInProgressTaskListQuery,
  useLazyGetHomeKpiCountsQuery,
  useLazyGetHomeMyProjectsQuery,
  useLazyGetHomeOverdueTaskListQuery,
} from "../../app/services/dashboardAPI";
import { useLazyGetUserByIdQuery, useUpdateUserMutation } from "../../app/services/userAPI";

import DashboardHeader from "./features/dashboardHeader/DashboardHeader";
import MyWork from "./features/myWork/MyWork";
import { mapHomeTaskRow } from "./features/myWork/myWork.utils";
import MyProjectsWidget from "./features/myProjectsWidget/MyProjectsWidget";

import "./Dashboard.scss";

const emptyLists = {
  overdue: [],
  dueToday: [],
  inProgress: [],
  completed: [],
};

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const userId = user?.userId;

  const [activeTab, setActiveTab] = useState("overdue");
  const [kpiCounts, setKpiCounts] = useState(null);
  const [listsByTab, setListsByTab] = useState(emptyLists);
  const [projects, setProjects] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState(profileSettings);
  const [profileData, setProfileData] = useState(null);

  const [getUserById, { data: userData }] = useLazyGetUserByIdQuery();
  const [getUserDetailByUserId, { data: isgetUserDetaileData }] = useLazyGetUserByIdQuery();
  const [updateUserDetailsById, { isLoading: isUpdateUserLoading, isSuccess: isUpdateUserSuccess, data: updatedUserData }] =
    useUpdateUserMutation();

  const [getHomeKpiCounts, { isFetching: isKpiFetching }] = useLazyGetHomeKpiCountsQuery();
  const [getHomeOverdueTaskList, { isFetching: isOverdueFetching }] = useLazyGetHomeOverdueTaskListQuery();
  const [getHomeDueTodayTaskList, { isFetching: isDueTodayFetching }] = useLazyGetHomeDueTodayTaskListQuery();
  const [getHomeInProgressTaskList, { isFetching: isInProgressFetching }] = useLazyGetHomeInProgressTaskListQuery();
  const [getHomeCompletedTaskList, { isFetching: isCompletedFetching }] = useLazyGetHomeCompletedTaskListQuery();
  const [getHomeMyProjects, { isFetching: isProjectsFetching }] = useLazyGetHomeMyProjectsQuery();

  const refreshDashboard = async () => {
    if (!userId) return;

    try {
      const [kpiData, overdue, dueToday, inProgress, completed, homeProjects] = await Promise.all([
        getHomeKpiCounts(userId).unwrap(),
        getHomeOverdueTaskList(userId).unwrap(),
        getHomeDueTodayTaskList(userId).unwrap(),
        getHomeInProgressTaskList(userId).unwrap(),
        getHomeCompletedTaskList(userId).unwrap(),
        getHomeMyProjects(userId).unwrap(),
      ]);

      if (Array.isArray(kpiData) && kpiData.length > 0) {
        setKpiCounts(kpiData[0]);
      } else if (kpiData && !Array.isArray(kpiData)) {
        setKpiCounts(kpiData);
      }

      setListsByTab({
        overdue: Array.isArray(overdue) ? overdue.map(mapHomeTaskRow) : [],
        dueToday: Array.isArray(dueToday) ? dueToday.map(mapHomeTaskRow) : [],
        inProgress: Array.isArray(inProgress) ? inProgress.map(mapHomeTaskRow) : [],
        completed: Array.isArray(completed) ? completed.map(mapHomeTaskRow) : [],
      });

      setProjects(Array.isArray(homeProjects) ? homeProjects : []);
    } catch (error) {
      // transformErrorResponse already surfaces toasts for common failures
    }
  };

  useEffect(() => {
    if (userId) {
      getUserById(userId);
      getUserDetailByUserId(userId);
      refreshDashboard();
    }
  }, [userId]);

  useEffect(() => {
    if (isUpdateUserSuccess && updatedUserData) {
      if (updatedUserData.errorMessage?.includes("exists")) {
        ToastService.warning(updatedUserData.errorMessage);
      } else {
        ToastService.success(updatedUserData.errorMessage);
        setShowPopup(false);
        getUserById(userId);
        getUserDetailByUserId(userId);
      }
    }
  }, [isUpdateUserSuccess, updatedUserData]);

  useEffect(() => {
    if (isgetUserDetaileData) {
      setProfileData(isgetUserDetaileData);
      setFormData((prev) => ({
        ...prev,
        initialState: {
          ...prev.initialState,
          profilePic: `data:image/png;base64,${isgetUserDetaileData.profilePicBase64}`,
          firstName: isgetUserDetaileData.firstName,
          lastName: isgetUserDetaileData.lastName,
          userName: isgetUserDetaileData.userName,
          emailAddress: isgetUserDetaileData.emailAddress,
          phone: isgetUserDetaileData.phone,
          gender: isgetUserDetaileData.gender,
        },
      }));
    }
  }, [isgetUserDetaileData]);

  const handleProfileSubmit = (userDetailData) => {
    if (!profileData) return;

    const requestData = {
      firstName: userDetailData.firstName,
      lastName: userDetailData.lastName,
      phone: userDetailData.phone,
      gender: userDetailData.gender,
      emailAddress: profileData.emailAddress,
      addressLineId: profileData.addressLineId,
      isActive: profileData.isActive,
      isResetPassword: profileData.isResetPassword,
      isSupperAdmin: profileData.isSupperAdmin,
      lastLoginDate: profileData.lastLoginDate,
      organizationId: profileData.organizationId,
      organizationLocation: profileData.organizationLocation,
      profilePic: userDetailData.profilePic[0]?.fileName || isgetUserDetaileData?.profilePic,
      profilePicBase64:
        userDetailData.profilePic[0]?.base64Data || isgetUserDetaileData?.profilePicBase64,
      base64File: userDetailData.profilePic[0]?.base64Data || isgetUserDetaileData?.profilePicBase64,
      reportingManagerName: profileData.reportingManagerName,
      repotingManager: profileData.reportingManager,
      roleId: profileData.roleId,
      roleName: profileData.roleName,
      state: profileData.state,
      storagePath: profileData.storagePath,
      userID: profileData.userID,
      userName: profileData.userName,
    };
    updateUserDetailsById(requestData);
  };

  const countsByTab = {
    overdue: listsByTab.overdue.length,
    dueToday: listsByTab.dueToday.length,
    inProgress: listsByTab.inProgress.length,
    completed: listsByTab.completed.length,
  };

  const myWorkLoading =
    isOverdueFetching || isDueTodayFetching || isInProgressFetching || isCompletedFetching;

  const profile = profileData || userData;

  return (
    <div className="dashboard">
      <div className="dashboard-shell">
        <div className="dashboard-shell__greeting">
          <DashboardHeader
            firstName={user?.firstName || user?.fullName?.split(" ")?.[0]}
            userId={userId}
            user={user}
            profile={profile}
            counts={isKpiFetching && !kpiCounts ? null : kpiCounts}
            showPopup={showPopup}
            formData={formData}
            isUpdateUserLoading={isUpdateUserLoading}
            onEdit={() => setShowPopup(true)}
            onClose={() => setShowPopup(false)}
            onSubmit={handleProfileSubmit}
          />
        </div>

        <div className="dashboard-shell__mywork">
          <MyWork
            activeTab={activeTab}
            onTabChange={setActiveTab}
            listsByTab={listsByTab}
            countsByTab={countsByTab}
            projects={projects}
            isLoading={myWorkLoading && listsByTab[activeTab]?.length === 0}
            isRefreshing={myWorkLoading || isProjectsFetching || isKpiFetching}
            onRefresh={refreshDashboard}
          />
        </div>

        <aside className="dashboard-shell__side">
          <MyProjectsWidget projects={projects} isLoading={isProjectsFetching && projects.length === 0} />
        </aside>
      </div>
    </div>
  );
};

export default Dashboard;
